import { ApiError } from '@/lib/api';
import { useCreateMcpToken } from '@/lib/mcp-tokens-queries';
import { MCP_TOKEN_NAME_MAX_LENGTH } from '@mss_creator/shared';
import {
  type CreatedMcpToken,
  MCP_TOKEN_LIMIT_REACHED,
  createMcpTokenBodySchema,
} from '@mss_creator/shared';
import {
  ActionBar,
  Alert,
  Button,
  Dialog,
  DialogClose,
  TextField,
} from '@pms-digital/design-system';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { ceilingMessage } from './mcp-ceiling';

const FORM_ID = 'mcp-token-create-form';
const FORM_HELP = `Pour reconnaître l'usage : poste, agent ou personne. ${MCP_TOKEN_NAME_MAX_LENGTH} caractères au plus.`;

interface McpTokenCreateDialogProps {
  /** The ceiling, for the message of a 409. */
  maxActive: number;
  /** Closes the dialog without creating anything. */
  onClose: () => void;
  /** The token was created: the caller shows its value once. */
  onCreated: (created: CreatedMcpToken) => void;
}

/**
 * The creation form: one field, Enter submits, the name is checked with the
 * shared Zod schema on submit. Escape and the close button leave; a click on
 * the backdrop does not (a half-typed name is not thrown away by a stray
 * click). The dialog is mounted only while open, so it always starts empty.
 */
export function McpTokenCreateDialog({ maxActive, onClose, onCreated }: McpTokenCreateDialogProps) {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const mutation = useCreateMcpToken();
  const pointerOutside = useRef(false);

  // Radix reports « close » the same way for Escape, the cross and a click on
  // the backdrop. The last input event tells them apart: the window listens in
  // the capture phase, so it sees the event before Radix's own listeners.
  useEffect(() => {
    const remember = (event: Event) => {
      pointerOutside.current =
        event.type === 'pointerdown' &&
        !(event.target instanceof Element && event.target.closest('[role="dialog"]'));
    };
    window.addEventListener('pointerdown', remember, true);
    window.addEventListener('keydown', remember, true);
    return () => {
      window.removeEventListener('pointerdown', remember, true);
      window.removeEventListener('keydown', remember, true);
    };
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mutation.isPending) return;
    const name = new FormData(event.currentTarget).get('name');
    const parsed = createMcpTokenBodySchema.safeParse({
      name: typeof name === 'string' ? name : '',
    });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Donnez un nom au jeton.');
      return;
    }
    setFieldError(null);
    mutation.mutate(parsed.data, {
      onSuccess: (created) => {
        onCreated(created);
        // The value now lives in the reveal dialog alone, not in the mutation cache.
        mutation.reset();
      },
    });
  }

  const apiError = mutation.error;
  const limitReached = apiError instanceof ApiError && apiError.code === MCP_TOKEN_LIMIT_REACHED;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending && !pointerOutside.current) onClose();
      }}
      size="form"
      title="Créer un jeton MCP"
      actions={
        <ActionBar
          secondary={
            <DialogClose asChild>
              <Button variant="ghost" disabled={mutation.isPending}>
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button
              variant="primary"
              type="submit"
              form={FORM_ID}
              icon="key"
              loading={mutation.isPending}
              loadingLabel="Création…"
            >
              Créer le jeton
            </Button>
          }
        />
      }
    >
      <form id={FORM_ID} noValidate onSubmit={handleSubmit} className="flex flex-col gap-4">
        <TextField
          name="name"
          label="Nom du jeton"
          placeholder="Claude Code - bureau"
          autoComplete="off"
          help={FORM_HELP}
          status={fieldError === null ? 'default' : 'error'}
          message={fieldError ?? undefined}
          onChange={() => setFieldError(null)}
        />
        {apiError && (
          <Alert tone="danger" title={limitReached ? undefined : 'La création a échoué.'}>
            {limitReached
              ? ceilingMessage(maxActive)
              : apiError instanceof ApiError && apiError.status === 400
                ? apiError.message
                : 'Aucun jeton n’a été créé. Réessayez dans un instant.'}
          </Alert>
        )}
      </form>
    </Dialog>
  );
}
